import { useMemo, useState, useEffect, useRef } from 'react';
import { TextField, Autocomplete, CircularProgress,} from '@mui/material';
import debounce from 'lodash.debounce';
import { useSearchParams } from 'react-router-dom';

export default function AuthorSelector({ value, onChange, minWidth = 220, urlKey = 'author' }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sp, setSp] = useSearchParams();

  const syncUrl = (usernameOrNull) => {
    const next = new URLSearchParams(sp);
    if (!usernameOrNull) next.delete(urlKey);         // ★ 비우면 쿼리 제거
    else next.set(urlKey, String(usernameOrNull));    // ★ 선택하면 쿼리 세팅
    setSp(next, { replace: true });
  };

  // 컴포넌트가 마운트되면 한번만 실행
  const hydratedOnce = useRef(false);

  useEffect(() => {
    if (hydratedOnce.current) return;                 // ★ 이미 했으면 종료
    let aborted = false;
    const usernameFromUrl =
      new URLSearchParams(window.location.search).get(urlKey); // ★ sp 의존 제거
    if (!usernameFromUrl || value?.username === usernameFromUrl) {
      hydratedOnce.current = true;
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `/api/users?query=${encodeURIComponent(usernameFromUrl)}&size=5`
        );
        if (!res.ok) throw new Error('user search failed');
        const data = await res.json();
        const list = data.content || data || [];
        const user = list.find((u) => u.username === usernameFromUrl) || null;

        if (!aborted) {
          onChange(user ?? { username: usernameFromUrl, userNick: usernameFromUrl });
        }
      } catch {
        if (!aborted) {
          onChange({ username: usernameFromUrl, userNick: usernameFromUrl });
        }
      } finally {
        hydratedOnce.current = true;    // ★ 더 이상 재하이드레이트 금지
      }
    })();

    return () => {
      aborted = true;
    };
  }, [value?.username, urlKey])
  
  const fetchUsers = async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/users?query=${encodeURIComponent(q || '')}&size=20`);
      const data = await res.json();
      setOptions(data.content || data || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debounced = useMemo(() => debounce(fetchUsers, 250), []);
  useEffect(() => { debounced(''); return () => debounced.cancel(); }, [debounced]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      onChange={(_, v)=>{
        onChange(v);
        syncUrl(v?.username ?? null);
      }}
      getOptionLabel={(o)=>o ? `${o.userNick ?? ''} (${o.username ?? ''})` : ''}
      // username 즉 아이디 기준 비교
      isOptionEqualToValue={(o,v)=>o?.username===v?.username}
      // 서버 사이드 검색이므로 클라이언트 필터링 끔
      filterOptions={(x) => x}
      // 리스트 아이템 Key 값을 username으로 설정
      renderOption={(props, option) => (
        <li {...props} key={option.username}>
          {/* 리스트 표시는 이름 + ID로 표시 */}
          {option.userNick ?? option.username} ({option.username})
        </li>
      )}
      renderInput={(params)=>(
        <TextField
          {...params}
          label="작성자"
          onChange={(e)=>debounced(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16}/> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      sx={{ minWidth }}
    />
  );
}